import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { syncService } from '../../services/syncService';
import { useNoAiStore } from '../../stores/noAiStore';
import { useSyncStore } from '../../stores/syncStore';
import { useToastStore } from '../../stores/toastStore';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { useTasksStore } from '../../stores/tasksStore';
import { useTenantStore } from '../../stores/tenantStore';
import { SyncPhase, SyncStatus, SyncType } from '../../types/sync/enums';
import { isPhaseAtOrAfter } from '../../utils/sync/syncPhase';
import { buildBridgeSyncPayload } from '../../utils/bridge/buildBridgeSyncPayload';
import { getEarliestAllowedDate, getTodayDate } from '../../utils/sync/syncStartDate';
import { HttpError } from '../../services/apiClient';
import { Button } from '../../ui/Button/Button';
import { DatePicker } from '../../ui/DatePicker/DatePicker';
import { PageTitle } from '../../ui/PageTitle/PageTitle';
import { SyncChecklist } from '../../components/SyncChecklist/SyncChecklist';
import { TranslationKey } from '../../i18n/translationKeys';
import { extractionIncompleteMessage } from '../../utils/bridge/extractionError';
import { bridgeSyncBlockReason } from '../../utils/bridge/bridgeSyncPreflight';
import { isBridgeOutdated } from '../../utils/bridge/bridgeVersion';
import { RelanceNoAccessNotice } from '../../components/RelanceNoAccessNotice/RelanceNoAccessNotice';

// 'bridge-unreachable'/'bridge-no-session' are no longer reachable via RootLayout in production —
// the app-wide bridge connection gate now blocks there before InitialSync ever mounts. Kept as a
// defensive fallback for this component's own direct callers/tests.
type ScreenState = 'idle' | 'triggering' | 'streaming' | 'error' | 'bridge-unreachable' | 'bridge-no-session' | 'bridge-outdated' | 'sync-blocked' | 'relance-no-access';

export const InitialSync: React.FC = () => {
    const { t } = useTranslation();
    const noAi = useNoAiStore(s => s.noAi);
    const setInitialSyncRequired = useSyncStore(s => s.setInitialSyncRequired);
    const showToast = useToastStore(s => s.showToast);
    const isAcomba = useIsAcomba();
    const checkBridgeHealth = useAcombaBridgeStore(s => s.checkBridgeHealth);
    const startExtraction = useAcombaBridgeStore(s => s.startExtraction);

    // Acomba is gated behind a Start button (so the start date can be chosen before the first sync);
    // AcombaGo/Connect auto-runs on mount (unchanged).
    const [screenState, setScreenState] = useState<ScreenState>(isAcomba ? 'idle' : 'triggering');
    const [snapshot, setSnapshot] = useState<{ phase: SyncPhase; completed: number; expected: number } | null>(null);
    // Holds the specific reason for a 'sync-blocked' screen (stale copy / company mismatch / incomplete
    // extraction) so it stays visible persistently rather than only in the transient toast.
    const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string>('');
    const [startDateError, setStartDateError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const runSync = useCallback(async () => {
        setScreenState('triggering');
        setSnapshot(null);

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            if (isAcomba) {
                // The InitialSync overlay replaces the app shell, so the bridge health poller
                // (BridgeWizard) isn't mounted here — verify the bridge/session directly, then read
                // the resulting store state rather than any value captured before the check.
                await checkBridgeHealth();
                const bridge = useAcombaBridgeStore.getState();
                if (bridge.bridgeStatus !== 'reachable') {
                    setScreenState('bridge-unreachable');
                    return;
                }
                if (bridge.sessionStatus !== 'connected') {
                    setScreenState('bridge-no-session');
                    return;
                }
                // Refuse the first sync when the bridge (bundled with Acomba) is out of date — the user
                // must update Acomba. Retry re-checks after they've updated and reopened.
                if (isBridgeOutdated(bridge.bridgeInfo?.bridgeVersion)) {
                    setScreenState('bridge-outdated');
                    return;
                }

                // Soft per-functionality gate: if the connected user lacks the Acomba access relance
                // needs, don't sync — show why and let them continue into the app (nothing to sync yet).
                if (bridge.functionalityAccess && !bridge.functionalityAccess.relance) {
                    setScreenState('relance-no-access');
                    return;
                }

                // Block (with feedback) when the sync can't produce a correct result: a stale copy would
                // push nothing, a company mismatch would push a foreign dataset. Both otherwise no-op
                // silently. Surface the specific reason on a persistent 'sync-blocked' screen — the
                // generic 'error' screen's "Retry" would just replay the same failure since nothing
                // changed (a different company / a stale copy).
                const blocked = bridgeSyncBlockReason(
                    { isStaleCopy: useTenantStore.getState().isStaleCopy, companyMatchStatus: bridge.companyMatchStatus },
                    t,
                );
                if (blocked) {
                    showToast('error', blocked);
                    setBlockedMessage(blocked);
                    setScreenState('sync-blocked');
                    return;
                }

                // Extract data from bridge. Abort (don't push) if any interface failed to read — a
                // partial payload would produce wrong relances, and a Full sync could wipe them.
                const failed = await startExtraction();
                // Null = the extraction never ran (already in flight, or a company mismatch). Must not
                // fall through to buildBridgeSyncPayload, which would post a previous run's leftovers.
                if (failed === null) {
                    setScreenState('error');
                    return;
                }
                if (failed.length > 0) {
                    const message = extractionIncompleteMessage(failed, t);
                    showToast('error', message);
                    setBlockedMessage(message);
                    setScreenState('sync-blocked');
                    return;
                }
                // startExtraction always reads every customer, so this payload is authoritative about absences.
                const payload = buildBridgeSyncPayload(SyncType.Full, true);

                if (!payload) {
                    setScreenState('error');
                    return;
                }

                try {
                    await syncService.triggerBridge(payload);
                } catch (e) {
                    // 409 = sync already in progress; subscribe to SSE anyway
                    if (!(e instanceof HttpError && e.status === 409)) {
                        setScreenState('error');
                        return;
                    }
                }
            } else {
                // AcceoConnect path
                // Soft gate mirroring the bridge check above: the login-time access check
                // (RelanceAccessGuard.CheckAndPersistConnectAccessAsync) already persisted
                // relanceAccessible before this screen ever mounted, so skip the (pointless) first
                // sync entirely when access is known to be denied. Unknown (null, not yet fetched)
                // is not blocked — same "unknown ≠ denied" rule as the bridge path above.
                await useTasksStore.getState().fetchDailyTasks();
                if (useTasksStore.getState().dailyTasks?.relanceAccessible === false) {
                    setScreenState('relance-no-access');
                    return;
                }

                try {
                    await syncService.triggerConnect(noAi, SyncType.Full);
                } catch (e) {
                    // 409 = sync already in progress; subscribe to SSE anyway (F-05.3)
                    if (!(e instanceof HttpError && e.status === 409)) {
                        setScreenState('error');
                        return;
                    }
                }
            }

            setScreenState('streaming');

            for await (const snap of syncService.streamProgress(controller.signal)) {
                setSnapshot({ phase: snap.phase, completed: snap.completed, expected: snap.expected });

                if (snap.status === SyncStatus.Completed) {
                    showToast('success', t('initialSync.toastSuccess'));
                    setInitialSyncRequired(false);
                    return;
                }
                if (snap.status === SyncStatus.Failed) {
                    setScreenState('error');
                    return;
                }
                // Content is usable from here: the figures are final and the ranked list exists, only
                // relance text keeps filling in behind the scenes. Dismiss now rather than waiting for
                // Completed — this component unmounts and RootLayout's useResumeSync takes over tracking
                // the still-running sync (it reattaches on initialSyncRequired flipping), so nothing
                // about the fill itself is lost, only handed off.
                if (isPhaseAtOrAfter(snap.phase, SyncPhase.RelanceFill)) {
                    showToast('success', t('initialSync.toastSuccess'));
                    setInitialSyncRequired(false);
                    return;
                }
            }
        } catch (e) {
            if ((e as Error).name === 'AbortError') return;
            setScreenState('error');
        }
    }, [noAi, setInitialSyncRequired, showToast, t, isAcomba, checkBridgeHealth, startExtraction]);

    // Persists the chosen start date (when set) before kicking off the sync, so the very first
    // sync honours it. An empty selection keeps the backend default (6-month lookback).
    const handleStart = useCallback(async () => {
        setStartDateError(null);

        if (startDate) {
            if (startDate < getEarliestAllowedDate()) {
                setStartDateError(t(TranslationKey.SyncStartDate.ErrorTooOld, { earliest: getEarliestAllowedDate() }));
                return;
            }
            try {
                await syncService.setSyncStartDate(startDate);
            } catch {
                setStartDateError(t(TranslationKey.SyncStartDate.ErrorSaveFailed));
                return;
            }
        }

        void runSync();
    }, [startDate, runSync, t]);

    // Acomba: load the configured start date for the picker and wait for the user to start explicitly
    // (so the chosen date applies to the first sync). AcombaGo/Connect: auto-run on mount as before.
    useEffect(() => {
        if (isAcomba) {
            syncService.getSyncStartDate()
                .then(({ startDate: current }) => setStartDate(current ?? ''))
                .catch(() => { /* leave empty — backend default applies */ });
        } else {
            void runSync();
        }
        return () => { abortRef.current?.abort(); };
        // Fires once on mount; runSync is stable enough for the auto-run path.
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const inProgress = screenState === 'triggering' || screenState === 'streaming';

    return (
        <div data-testid="initial-sync-screen" className="flex flex-col items-center justify-center min-h-screen gap-xl px-l">
            {!inProgress && <PageTitle data-testid="initial-sync-title">{t('initialSync.title')}</PageTitle>}

            {screenState === 'idle' ? (
                <div data-testid="initial-sync-start" className="flex flex-col items-center gap-l w-full max-w-md">
                    <div className="w-full flex flex-col items-center gap-s text-center">
                        <DatePicker
                            id="initial-sync-start-date"
                            data-testid="initial-sync-start-date-input"
                            label={t(TranslationKey.SyncStartDate.DateLabel)}
                            value={startDate}
                            min={getEarliestAllowedDate()}
                            max={getTodayDate()}
                            onChange={(e) => { setStartDate(e.target.value); setStartDateError(null); }}
                            className="w-48"
                        />
                        <p className="text-primary-600 text-s">{t(TranslationKey.SyncStartDate.DefaultDescription)}</p>
                        {startDateError && (
                            <p data-testid="initial-sync-start-date-error" className="text-danger text-s">{startDateError}</p>
                        )}
                    </div>
                    <Button data-testid="initial-sync-start-btn" onClick={() => void handleStart()}>
                        {t(TranslationKey.InitialSync.StartButton)}
                    </Button>
                </div>
            ) : screenState === 'bridge-unreachable' ? (
                <div data-testid="initial-sync-bridge-unreachable" className="flex flex-col items-center gap-l">
                    <p className="text-danger font-normal">{t(TranslationKey.InitialSync.BridgeUnreachableError)}</p>
                    <Button data-testid="initial-sync-bridge-unreachable-retry-btn" onClick={() => void runSync()}>
                        {t(TranslationKey.InitialSync.BridgeUnreachableRetry)}
                    </Button>
                </div>
            ) : screenState === 'bridge-no-session' ? (
                <div data-testid="initial-sync-bridge-no-session" className="flex flex-col items-center gap-l">
                    <p className="text-danger font-normal">{t(TranslationKey.InitialSync.BridgeNoSessionError)}</p>
                    <Button data-testid="initial-sync-bridge-no-session-retry-btn" onClick={() => void runSync()}>
                        {t(TranslationKey.InitialSync.BridgeNoSessionRetry)}
                    </Button>
                </div>
            ) : screenState === 'bridge-outdated' ? (
                <div data-testid="initial-sync-bridge-outdated" className="flex flex-col items-center gap-l max-w-md text-center">
                    <p className="text-danger font-normal">{t(TranslationKey.InitialSync.BridgeOutdatedError)}</p>
                    <Button data-testid="initial-sync-bridge-outdated-retry-btn" onClick={() => void runSync()}>
                        {t(TranslationKey.InitialSync.BridgeOutdatedRetry)}
                    </Button>
                </div>
            ) : screenState === 'sync-blocked' ? (
                <div data-testid="initial-sync-blocked" className="flex flex-col items-center gap-l">
                    <p data-testid="initial-sync-blocked-message" className="text-danger font-normal text-center">{blockedMessage}</p>
                    <Button data-testid="initial-sync-blocked-retry-btn" onClick={() => void runSync()}>
                        {t('initialSync.retryButton')}
                    </Button>
                </div>
            ) : screenState === 'relance-no-access' ? (
                <RelanceNoAccessNotice
                    testId="initial-sync-relance-no-access"
                    continueButtonTestId="initial-sync-relance-no-access-continue-btn"
                    onContinue={() => setInitialSyncRequired(false)}
                />
            ) : screenState === 'error' ? (
                <div data-testid="initial-sync-error" className="flex flex-col items-center gap-l">
                    <p className="text-danger font-normal">{t('initialSync.errorTitle')}</p>
                    <Button data-testid="initial-sync-retry-btn" onClick={() => void runSync()}>
                        {t('initialSync.retryButton')}
                    </Button>
                </div>
            ) : (
                <div data-testid="initial-sync-progress" className="w-full max-w-md">
                    <SyncChecklist phase={snapshot?.phase} completed={snapshot?.completed ?? 0} expected={snapshot?.expected ?? 0} />
                </div>
            )}
        </div>
    );
};
